import React, { HTMLAttributes, useCallback } from 'react';

import PropTypes from 'prop-types';

import {
  getCharacterCountText,
  useForwardedRef,
} from '@adsk/alloy-react-helpers';
import { stylePropType, StyleProp } from '@adsk/alloy-react-theme';

import EditButtonGroup from './EditButtonGroup';
import { InputWrapperProps, InputProps } from './types';
import useInlineInput from './useInlineInput';

// role was intoduced for v17 and breaks projects using v16 types
// https://github.com/DefinitelyTyped/DefinitelyTyped/pull/53361
type InlineTextInputProps = Omit<
  InputWrapperProps<string>,
  'onChange' | 'role'
> & {
  saveUnmodifiedValue?: boolean;
  cancelOnBlur?: boolean;
  inputProps?: InputProps;
  softLimit?: number;
  maxLength?: number;
  showCount?: boolean;
  showCountThreshold?: number;
};

/**
 * A TextInput for the InlineEdit component.
 */
const InlineTextInput = React.forwardRef<
  HTMLInputElement,
  InlineTextInputProps
>(
  (
    {
      placeholder,
      value,
      error,
      id,
      saveUnmodifiedValue = false,
      cancelOnBlur = false,
      inputProps,
      softLimit,
      maxLength,
      showCount,
      showCountThreshold = 15,
      ...props
    },
    ref,
  ) => {
    const inputRef = useForwardedRef(ref);
    const {
      getInputProps,
      getWrapperProps,
      handleCancel,
      handleSubmit,
      modified,
      charCount,
      shouldRenderCount,
      inputWrapperTokens,
      inputTokens,
      characterCountTokens,
      editButtonTokens,
    } = useInlineInput<HTMLInputElement, string | undefined>({
      inputRef,
      value,
      saveUnmodifiedValue,
      cancelOnBlur,
      error,
      softLimit,
      maxLength,
      showCount,
      showCountThreshold,
    });

    const wrapperProps = getWrapperProps(props);

    const renderCount = useCallback(
      (props?: HTMLAttributes<HTMLDivElement>) => {
        if (shouldRenderCount) {
          return (
            <div
              {...props}
              css={[characterCountTokens]}
              data-testid="InlineTextInput__count"
            >
              {getCharacterCountText({ charCount, softLimit, maxLength })}
            </div>
          );
        }

        return null;
      },
      [
        softLimit,
        charCount,
        maxLength,
        shouldRenderCount,
        characterCountTokens,
      ],
    );

    return (
      <div {...wrapperProps} css={inputWrapperTokens}>
        <input
          {...getInputProps({
            placeholder,
            id,
            style: wrapperProps.style.input as StyleProp | undefined,
            ...inputProps,
            maxLength,
          })}
          css={inputTokens}
        />
        {renderCount()}
        <EditButtonGroup
          handleCancel={handleCancel}
          handleSubmit={handleSubmit}
          modified={modified}
          editButtonTokens={editButtonTokens}
          error={error}
        />
      </div>
    );
  },
);

InlineTextInput.displayName = 'InlineTextInput';

InlineTextInput.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Placeholder text value */
  placeholder: PropTypes.string,
  /** Input id */
  id: PropTypes.string,
  /** The value of the field */
  value: PropTypes.string,
  /** If true the initial value can be saved without being modified, onChange will be fired when saving.
   * By default, the user must modify the value before it can be saved */
  saveUnmodifiedValue: PropTypes.bool,
  /** If true the initial value will be restored when modified input is blurred */
  cancelOnBlur: PropTypes.bool,
  /** Additional props to pass to the input element */
  inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /** Like maxLength but this is a soft threshold and the input does not stop the user from entering characters beyond this amount, but this will show as the maximum number in the count display */
  softLimit: PropTypes.number,
  /** A hard limit on the maximum number of characters permitted in the input field, if softLimit is not provided then this will show as the maximum number in the count display */
  maxLength: PropTypes.number,
  /** If true and maxLength or softLimit is set then display a character count, otherwise do not display the character count */
  showCount: PropTypes.bool,
  /** If set, this will delay the display of character count until the character count is within this many characters of reaching the maximum length or soft limit length. Default is 15. Set to 0 to always show the count when showCount is enabled. */
  showCountThreshold: PropTypes.number,
};

export default InlineTextInput;
